import { ReactNode, useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './components/Sheet';

function useIsDesktop(minWidth = 1024) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(min-width: ${minWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [minWidth]);

  return isDesktop;
}

// Arrow SVG icons
const ArrowLeftSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 28 28">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M5.833 14h16.334m-10.5-5.833L5.833 14m5.834 5.833L5.833 14"
    />
  </svg>
);

const ArrowRightSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 28 28">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M22.167 14H5.833m10.5 5.833L22.167 14m-5.834-5.833L22.167 14"
    />
  </svg>
);

// More options icon for mobile details (Material Symbols - more_horiz)
const MoreHorizSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 -960 960 960" fill="currentColor">
    <path d="M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z" />
  </svg>
);

export interface TableColumn<T = unknown> {
  /** Unique identifier for the column */
  key: string;
  /** Column header label */
  header: string;
  /** Column header text color */
  headerTextColor?: string;
  /** Render function for cell content */
  render: (row: T, rowIndex: number) => ReactNode;
  /** Optional width for the column (CSS value) */
  width?: string;
  /** Optional alignment for the column */
  align?: 'left' | 'center' | 'right';
  /** Optional font size for the column (CSS value) */
  fontSize?: string;
}

export interface PaginationConfig {
  /** Current page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items */
  totalItems: number;
  /** Items per page */
  pageSize: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Optional page size options (e.g., [10, 20, 50]). If provided, shows a page size selector */
  pageSizeOptions?: number[];
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
}

export interface TableProps<T = unknown> {
  /** Array of column definitions */
  columns: TableColumn<T>[];
  /** Array of data rows */
  data: T[];
  /** Optional pagination configuration */
  pagination?: PaginationConfig;
  /** Optional className for the table container */
  className?: string;
  /** Optional empty state message */
  emptyMessage?: string;
  /**
   * Column keys to show on mobile (main columns).
   * If not provided, falls back to card layout with all columns.
   * A "Detalhes" column is automatically added to view all details in a sheet.
   */
  mobileColumnKeys?: string[];
}

/**
 * Pagination component for the table
 */
function TablePagination({ pagination, isDesktop }: { pagination: PaginationConfig; isDesktop: boolean }) {
  const { currentPage, totalPages, totalItems, pageSize, onPageChange, pageSizeOptions, onPageSizeChange } = pagination;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Calculate visible page numbers - always returns exactly 5 slots for consistency
  const getPageNumbers = (): (number | 'ellipsis-start' | 'ellipsis-end')[] => {
    // If 5 or fewer pages, show all
    if (totalPages <= 5) {
      const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Always show 5 elements: [1] [...] [middle] [...] [last]
    // But adjust based on current page position
    if (currentPage <= 3) {
      // Near start: show [1] [2] [3] [...] [last]
      return [1, 2, 3, 'ellipsis-end', totalPages];
    } else if (currentPage >= totalPages - 2) {
      // Near end: show [1] [...] [n-2] [n-1] [n]
      return [1, 'ellipsis-start', totalPages - 2, totalPages - 1, totalPages];
    } else {
      // Middle: show [1] [...] [current] [...] [last]
      return [1, 'ellipsis-start', currentPage, 'ellipsis-end', totalPages];
    }
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex h-[52px] items-center justify-between rounded-[5px] bg-[#191919] px-4">
      {/* Left side: "Mostrando..." text + optional page size selector */}
      {isDesktop && (
        <div className="desktop-only-flex min-w-[200px] items-center gap-4">
          <span className="text-caption whitespace-nowrap text-[var(--color-neutral-200)]">
            Mostrando {startItem}-{endItem} de {totalItems} itens
          </span>
          {pageSizeOptions && onPageSizeChange && (
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="text-caption h-[28px] cursor-pointer rounded-[5px] border border-[var(--color-neutral-600)] bg-[var(--color-neutral-900)] px-2 text-[var(--color-neutral-100)] transition-all outline-none hover:border-[var(--color-neutral-300)]"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} / página
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="flex w-full items-center justify-end gap-3 lg:w-auto lg:justify-end lg:gap-6">
        {/* Previous button - icon only on mobile, text on desktop */}
        <button
          type="button"
          className="text-caption flex h-[32px] cursor-pointer items-center gap-1 rounded-[5px] border-none bg-[var(--color-neutral-900)] px-2 text-[var(--color-neutral-100)] transition-all hover:bg-[var(--color-neutral-600)] disabled:cursor-not-allowed disabled:text-[var(--color-neutral-600)] disabled:hover:bg-[var(--color-neutral-900)] lg:px-3"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ArrowLeftSvg />
          {isDesktop && <span className="desktop-only-inline">Anterior</span>}
        </button>

        {/* Page number buttons */}
        <div className="flex items-center gap-2">
          {pageNumbers.map((page) => {
            // Handle ellipsis markers
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <span
                  key={page}
                  className="text-caption flex h-[32px] min-w-[32px] items-center justify-center text-[var(--color-neutral-200)]"
                >
                  ...
                </span>
              );
            }

            // Current page - not clickable, no hover effect
            if (currentPage === page) {
              return (
                <span
                  key={page}
                  className="text-caption flex h-[32px] min-w-[32px] items-center justify-center rounded-[5px] border-none bg-[var(--color-primary)] px-2 text-[var(--color-neutral-50)]"
                >
                  {page}
                </span>
              );
            }

            // Other pages - clickable
            return (
              <button
                key={page}
                type="button"
                className="text-caption flex h-[32px] min-w-[32px] cursor-pointer items-center justify-center rounded-[5px] border border-[var(--color-neutral-600)] bg-transparent px-2 text-[var(--color-neutral-600)] transition-all hover:border-[var(--color-neutral-300)] hover:text-[var(--color-neutral-300)]"
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Next button - icon only on mobile, text on desktop */}
        <button
          type="button"
          className="text-caption flex h-[32px] cursor-pointer items-center gap-1 rounded-[5px] border-none bg-[var(--color-neutral-900)] px-2 text-[var(--color-neutral-100)] transition-all hover:bg-[var(--color-neutral-600)] disabled:cursor-not-allowed disabled:text-[var(--color-neutral-600)] disabled:hover:bg-[var(--color-neutral-900)] lg:px-3"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {isDesktop && <span className="desktop-only-inline">Próximo</span>}
          <ArrowRightSvg />
        </button>
      </div>
    </div>
  );
}

/**
 * Mobile Card Row - displays table row as a card on mobile (legacy fallback)
 */
function MobileCardRow<T>({ row, columns, rowIndex }: { row: T; columns: TableColumn<T>[]; rowIndex: number }) {
  return (
    <div className="rounded-lg bg-[var(--color-neutral-900)] p-4">
      {columns.map((column) => (
        <div key={column.key} className="mb-3 last:mb-0">
          <div className="text-caption text-[var(--color-neutral-200)]">{column.header}</div>
          <div className="text-body mt-1 text-[var(--color-neutral-100)]">{column.render(row, rowIndex)}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Mobile Details Sheet - displays all row details in a bottom sheet
 */
function MobileDetailsSheet<T>({
  isOpen,
  onClose,
  row,
  columns,
  rowIndex,
}: {
  isOpen: boolean;
  onClose: () => void;
  row: T | null;
  columns: TableColumn<T>[];
  rowIndex: number;
}) {
  if (!row) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>Detalhes</SheetTitle>
        </SheetHeader>
        <div className="sheet-details-content space-y-4 px-4 pt-2 pb-4">
          {columns.map((column) => (
            <div key={column.key} className="border-b border-[var(--color-neutral-900)] pb-4 last:border-b-0">
              <div className="text-caption mb-1 text-[var(--color-neutral-200)]">{column.header}</div>
              <div className="text-body text-[var(--color-neutral-100)]">{column.render(row, rowIndex)}</div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Table component for displaying tabular data with optional pagination
 *
 * Features:
 * - Customizable columns with render functions
 * - Optional pagination
 * - Responsive design (table on desktop, simplified table on mobile)
 * - Mobile: shows main columns + details button that opens a sheet with all info
 * - Empty state support
 */
export function Table<T = unknown>({
  columns,
  data,
  pagination,
  className = '',
  emptyMessage,
  mobileColumnKeys,
}: TableProps<T>) {
  const [sheetData, setSheetData] = useState<{ row: T; rowIndex: number } | null>(null);
  const isDesktop = useIsDesktop();
  const isSheetOpen = sheetData !== null;

  if (data.length === 0) {
    return (
      <div className={`flex min-h-[200px] items-center justify-center ${className}`}>
        <p className="text-body text-[var(--color-neutral-200)]">{emptyMessage || 'Nenhum item encontrado'}</p>
      </div>
    );
  }

  // Filter columns for mobile view based on mobileColumnKeys
  const mobileColumns = mobileColumnKeys ? columns.filter((col) => mobileColumnKeys.includes(col.key)) : columns;

  // Check if we should use the new mobile table view (when mobileColumnKeys is provided)
  const useMobileTable = !!mobileColumnKeys;

  return (
    <div className={`space-y-4 ${className}`}>
      {isDesktop ? (
        <div className="desktop-only-table">
          <table className="w-full border-separate border-spacing-0" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="p-2 pb-4 text-left font-normal"
                    style={{
                      width: column.width,
                      maxWidth: column.width,
                      textAlign: column.align || 'left',
                      fontSize: column.fontSize || '16px',
                      color: column.headerTextColor || 'var(--color-neutral-200)',
                    }}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="p-2 text-[var(--color-neutral-100)]"
                      style={{
                        textAlign: column.align || 'left',
                        width: column.width,
                        maxWidth: column.width,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div className="table-cell-content">{column.render(row, rowIndex)}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mobile-only-table">
          {useMobileTable ? (
            <table className="w-full table-auto border-separate border-spacing-0">
              <thead>
                <tr>
                  {mobileColumns.map((column, idx) => (
                    <th
                      key={column.key}
                      className="pr-3 pb-3 text-left text-xs font-normal text-[var(--color-neutral-200)] last:pr-0"
                      style={{
                        width: idx === mobileColumns.length - 1 ? 'auto' : undefined,
                        textAlign: column.align || 'left',
                      }}
                    >
                      {column.header}
                    </th>
                  ))}
                  <th className="w-[10%] pb-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {mobileColumns.map((column) => (
                      <td
                        key={column.key}
                        className="py-2 pr-3 text-xs text-[var(--color-neutral-100)] last:pr-0"
                        style={{
                          textAlign: column.align || 'left',
                          fontSize: column.fontSize || '16px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column.render(row, rowIndex)}
                      </td>
                    ))}
                    <td className="w-[10%] py-2 text-center">
                      <button
                        type="button"
                        className="inline-flex h-7 w-7 cursor-pointer items-center justify-center border-none bg-transparent text-[var(--color-primary-medium)] transition-all hover:text-[var(--color-primary)]"
                        onClick={() => setSheetData({ row, rowIndex })}
                        aria-label="Ver detalhes"
                      >
                        <MoreHorizSvg />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="space-y-3">
              {data.map((row, rowIndex) => (
                <MobileCardRow key={rowIndex} row={row} columns={columns} rowIndex={rowIndex} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mobile Details Sheet */}
      <MobileDetailsSheet
        isOpen={isSheetOpen}
        onClose={() => setSheetData(null)}
        row={sheetData?.row ?? null}
        columns={columns}
        rowIndex={sheetData?.rowIndex ?? 0}
      />

      {/* Pagination */}
      {pagination && <TablePagination pagination={pagination} isDesktop={isDesktop} />}
    </div>
  );
}
